import { lazy } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'dibs-react-intl';
import { Locale } from 'dibs-intl/exports/locales';
import { clientIntlPolyfill } from 'dibs-buyer-layout/exports/clientIntlPolyfill';
import { CheckoutServerVarsContextProvider } from '../components/shared/CheckoutServerVarsContext';
import { wait } from '../helpers/wait';
import { PRERENDER_CHECKOUT_ROOT_ID } from './constants';
import {
    addStoreUpdateListener,
    getPrerenderedCheckoutState,
    setPrerenderedCheckoutRootState,
    resetPreRenderedCheckoutState,
    PRE_RENDER_STATE_VISIBLE,
    PRE_RENDER_STATE_PENDING,
    PRE_RENDER_STATE_ENABLED_IN_BACKGROUND,
    PRE_RENDER_STATE_NOT_INITIALIZED,
    PRE_RENDER_STATE_INITIAL,
} from './prerenderedCheckoutStore';
import { handleBrowserButtons } from './redirectToPrerenderedCheckout';
import { createCheckoutEnvironment } from '../environment';
import { resetHideKlarnaOnCheckoutTracking } from '../helpers/hideKlarnaAbTestHelpers';
import SV from 'server-vars';

const CheckoutRoot = lazy(() => import(/* webpackChunkName: "CheckoutRoot" */ '../CheckoutRoot'));

const TWO_SECONDS = 2000;
let PRE_RENDER_DELAY = TWO_SECONDS;

export type InitializePrerenderedCheckoutParams = {
    locale: Locale;
    messages: Record<string, string> | undefined;
    isMobile: boolean;
    itemId: string;
};

let lastParams: InitializePrerenderedCheckoutParams | null = null;
let reactRoot: Root | null = null;
let abortControllerList: AbortController[] = [];

function removeAbortController(controller: AbortController): void {
    abortControllerList = abortControllerList.filter(
        savedController => savedController !== controller
    );
}
function saveAbortController(controller: AbortController): void {
    abortControllerList.push(controller);
}

export const initializePrerenderedCheckout = async (
    params: InitializePrerenderedCheckoutParams
): Promise<void> => {
    lastParams = params;
    const { locale, messages, isMobile, itemId } = params;

    if (!SV.get('enablers.preRenderCheckout')) {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_NOT_INITIALIZED);
        return;
    }

    if (getPrerenderedCheckoutState().rootState !== PRE_RENDER_STATE_INITIAL) {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_NOT_INITIALIZED);
        throw Error(
            `<div id="${PRERENDER_CHECKOUT_ROOT_ID}" /> has already been created. "initializePrerenderedCheckout" function can not be called twice`
        );
    }

    const controller = new AbortController();
    saveAbortController(controller);

    const checkoutRootElement = document.createElement('div');
    checkoutRootElement.setAttribute('id', PRERENDER_CHECKOUT_ROOT_ID);
    document.getElementById('mainContent')?.appendChild(checkoutRootElement);

    handleBrowserButtons();

    /**
     * Timeout to give time PDP time to load, we don't want to slow things down.
     */
    const { promise, cancel } = wait(PRE_RENDER_DELAY);

    const removeStoreUpdateListener = addStoreUpdateListener(state => {
        if (state.rootState !== PRE_RENDER_STATE_PENDING) {
            cancel();
        }
    });
    await promise;
    removeStoreUpdateListener();
    if (getPrerenderedCheckoutState().rootState === PRE_RENDER_STATE_PENDING) {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_ENABLED_IN_BACKGROUND);
    }

    await clientIntlPolyfill();

    const { createCheckoutStore } = await import(
        /* webpackChunkName: "CheckoutRootReducer" */ '../reducers/root'
    );

    reactRoot = createRoot(checkoutRootElement, { identifierPrefix: 'abc' });

    /**
     * Async work is done, check if someone triggered an abort.
     *
     * THIS SHOULD ALWAYS HAPPEN AFTER ASYNC CODE!
     */
    if (controller.signal.aborted) {
        removeAbortController(controller);
        return;
    }

    reactRoot.render(
        <IntlProvider locale={locale} messages={messages}>
            <CheckoutServerVarsContextProvider isMobile={isMobile} checkoutType="page">
                <Provider store={createCheckoutStore()}>
                    <CheckoutRoot
                        itemId={itemId}
                        environment={createCheckoutEnvironment({
                            getGraphQLNetworkContext: () => {
                                if (
                                    getPrerenderedCheckoutState().rootState ===
                                    PRE_RENDER_STATE_VISIBLE
                                ) {
                                    return 'checkout';
                                }
                                return 'prerender-checkout';
                            },
                        })}
                    />
                </Provider>
            </CheckoutServerVarsContextProvider>
        </IntlProvider>
    );
    removeAbortController(controller);
};

export function removeCheckout(): void {
    /**
     * If the user is quick to exit the page, there might be a pending checkout initialization that
     * need to be cancelled. e.g. quickly leaving a conversation view and going the the inbox.
     *
     * If we would not abort the pending checkout initialization, there could be multiple instances
     * of pre-renderd checkout on the page.
     */
    abortControllerList.forEach(controller => controller.abort());
    resetPreRenderedCheckoutState();
    reactRoot?.unmount();

    // Reset Hide Klarna on Checkout test tracking,
    // so it tracks again if pre-rendered checkout or checkout in modal is opened.
    resetHideKlarnaOnCheckoutTracking();
}

export function refreshCheckout(): void {
    const checkoutRootElement = document.getElementById(PRERENDER_CHECKOUT_ROOT_ID);
    if (!lastParams || !checkoutRootElement) {
        /**
         * Do nothing because checkout was never pre-rendered.
         */
        return;
    }
    removeCheckout();
    /**
     * This wouldn't be called on page load, so we no longer need worry about slowing
     * down other requests.
     */
    PRE_RENDER_DELAY = 0;
    initializePrerenderedCheckout(lastParams);
}
